import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, PrefKey, Preferences, Project, ShortlistedUnit, ThreadReceipt, WorkNote } from '@/lib/types';
import {
  INITIAL_MESSAGES,
  applyPref,
  freeformReply,
  multiPrefSummary,
  nextId,
  prefAckAfterMultiStep,
  prefAckMessages,
  recommendationMessages,
  revealConfirmedMessage,
  shortlistAddedMessage,
  toggleMultiPrefValue,
  unitShortlistAddedMessage,
} from '@/lib/chat-engine';
import { BRIEF_OPTIONS, buildBriefOptions, emptyPreferences, findBriefChipPref, formatBriefForBot, isBriefReady, isMultiBriefKey, nextBriefKey, prefsToAdvisorPreferences, pruneConfigSelections, type BriefOptionsMap, type MultiBriefPref, isDeepeningPending } from '@/lib/brief-options';
import { enrichProjects, type PrototypeProjectDetail } from '@/lib/prototype-catalog';
import { filterCatalogByPrefs } from '@/lib/catalog-filter';
import { projectWithLiveDetail, projectFromBookedVisit } from '@/lib/live-project';
import {
  fetchAdvisorBriefFacets,
  fetchAdvisorProjectDetail,
  getAdvisorMode,
  sendAdvisorTurn,
  type AdvisorProjectCard,
  type AdvisorProjectDetail,
  type AdvisorUiMode,
  type BookedVisit,
  type CompareMatrixPayload,
  type SearchRecoveryPayload,
  type VisitQueuePayload,
} from '@/lib/advisor-api';
import type { VisitItineraryPayload } from '@/lib/visit-itinerary';
import {
  buildCompareMatrixFromProjects,
  chatReplyForCompare,
} from '@/lib/compare-table';
import { chatReplyForBoardVisit, isMultiVisitRequestTurn, isVisitBoardSchedulingTurn, isVisitRouteExpandTurn, isVisitSchedulingGoal, resolveVisitDateFromText, visitBoardPlanAck, visitBoardSchedulingReply } from '@/lib/visit-board';
import { buildVisitRouteStopIds } from '@/lib/visit-queue';
import { VISIT_DATES } from '@/lib/prototype-catalog';
import { boardRouteAction, boardRouteFromNba, resolveProjectMention } from '@/lib/board-route';
import type { AdvisorNba, AdvisorChecklistSnapshot } from '@/lib/advisor-api';
import { isGlobalAdvisorTurn, projectRefsFromCatalog, resolveMultiProjectMentions, resolveTurnFocus } from '@/lib/engaged-projects';
import {
  applyRefinePatch,
  exploreMoreRefineReply,
  extractLocationRefinement,
  isLocationExpandTurn,
  isSearchRefinementTurn,
  isSearchRefinementUserLine,
  isExploreMoreTurn,
  nextBudgetTier,
  parseRecoverySearch,
  parseRefineChip,
  refineSearchAck,
  resolveSuggestedAction,
  shouldClearMatchFocus,
  isFocusedPoiAsk,
} from '@/lib/search-refine';
import { messagesForTool } from '@/lib/tool-actions';
import type { ToolKind } from '@/lib/types';
import {
  getOrCreateSessionId,
  loadPersistedState,
  savePersistedState,
  sessionToBuyerPhone,
  startNewAdvisorSession,
} from '@/lib/session';

export type DrawerKind = 'plans' | 'shortlist' | 'compare';

export type AppView = 'home' | 'app';
export type BoardView = 'welcome' | 'matches' | 'project' | 'compare' | 'visit' | 'concierge';
export type ProjectTab = 'legal' | 'units' | 'price' | 'emi';

export interface BoardState {
  view: BoardView;
  projectId: string;
  tab: ProjectTab;
  floor: number;
  unitNo: string | null;
}

export interface DrawerState {
  kind: DrawerKind | null;
  projectId?: string;
  unitId?: string | null;
}

interface AdvisorState {
  sessionId: string;
  appView: AppView;
  board: BoardState;
  revealed: boolean;
  visitDate: string | null;
  visitSkipped: Record<string, boolean>;
  engagedProjectIds: string[];
  bookedVisits: BookedVisit[];
  visitQueue: VisitQueuePayload | null;
  visitItinerary: VisitItineraryPayload | null;
  visitRouteStopIds: string[];
  visitAwaitingConfirm: boolean;
  emi: { down: number; rate: number; tenure: number };
  /** Buyer engaged the EMI tool this session (tab opened or sliders moved). */
  emiTouched: boolean;
  matchProjects: PrototypeProjectDetail[];
  messages: ChatMessage[];
  prefs: Preferences;
  shortlist: Project[];
  unitShortlist: ShortlistedUnit[];
  isLoading: boolean;
  /** True while a brief→results dispatch is in flight — drives the shortlisting strip. */
  shortlisting: boolean;
  /** Real catalog size (brief-facets project_count); honest start anchor for the narrowing count. */
  catalogCount: number;
  mode: 'demo' | 'live';
  briefReady: boolean;
  briefPhase: 'collecting' | 'matched' | 'focused';
  uiMode: AdvisorUiMode;
  preferenceEditingActive: boolean;
  lastSearchRecovery: SearchRecoveryPayload | null;
  /** P7 — server nba.chips when present (live). */
  lastNbaChips: string[] | null;
  /** ADR-006: chip label → action_id for deterministic taps (null when none). */
  lastNbaChipActions: Record<string, string> | null;
  /** Raw server prefs_snapshot for the understanding tray (live). */
  signalSnapshot: Record<string, string | undefined> | null;
  activeMultiPref: PrefKey | null;
  briefOptions: BriefOptionsMap;
  projectDetails: Record<string, AdvisorProjectDetail>;
  projectDetailLoading: string | null;
  compareMatrix: CompareMatrixPayload | null;
  focusedProject: PrototypeProjectDetail | null;
  engagedProjects: Project[];
  getBoardProject: (projectId: string) => PrototypeProjectDetail;
  drawer: DrawerState;
  /** One-Thread: the board sheet (summoned surface) open state. */
  boardSheetOpen: boolean;
  setBoardSheetOpen: (open: boolean) => void;
  /** Leave a receipt line in the thread when a summoned sheet closes. */
  addReceipt: (receipt: ThreadReceipt) => void;
  begin: () => void;
  /** Landing ask-bar handoff: enter the app and send `text` through the normal composer path. */
  beginWithAsk: (text: string) => void;
  goHome: () => void;
  setBoardView: (view: BoardView) => void;
  showAllMatches: (opts?: { clearFocus?: boolean }) => void;
  /** Leave focus and re-sync the engine to the matches hub (chip + nav). */
  returnToMatchesHub: () => void;
  exploreMoreProjects: (actionId?: string) => void;
  compareAllMatches: (actionId?: string) => void;
  openCompareBoard: (matrix?: CompareMatrixPayload | null) => void;
  switchBoardFocus: (projectId: string) => void;
  openBoardProject: (projectId: string, tab?: ProjectTab) => void;
  setProjectTab: (tab: ProjectTab) => void;
  setProjectFloor: (floor: number) => void;
  pickUnit: (unitNo: string | null) => void;
  setVisitDate: (id: string | null) => void;
  toggleVisitStop: (projectId: string) => void;
  planVisitDay: (actionId?: string) => void;
  /** @deprecated use planVisitDay */
  planSelectedVisits: () => void;
  refineFromChip: (label: string) => boolean;
  /** P7 rail — leave focus, enter preference refine (area/budget/brief redo). */
  beginBriefRefine: (hint?: 'area' | 'budget' | 'widen' | 'general') => void;
  setEmiField: (field: 'down' | 'rate' | 'tenure', value: number) => void;
  setPref: (pref: PrefKey, value: string) => void;
  toggleMultiPref: (pref: MultiBriefPref, value: string) => void;
  confirmMultiPrefStep: (pref: MultiBriefPref) => void;
  sendText: (text: string, actionId?: string) => void;
  addToShortlist: (project: Project) => void;
  removeFromShortlist: (projectId: string) => void;
  addUnitToShortlist: (entry: ShortlistedUnit) => void;
  removeUnitFromShortlist: (unitId: string) => void;
  isUnitShortlisted: (unitId: string) => boolean;
  askCompare: () => void;
  showRecommendations: () => void;
  confirmReveal: (project?: Project) => void;
  runTool: (projectId: string | undefined, kind: ToolKind) => void;
  openPlansDrawer: (projectId: string, unitId?: string | null) => void;
  openShortlistDrawer: () => void;
  openCompareDrawer: () => void;
  closeDrawer: () => void;
  resetSession: () => void;
  buyerPhone: string;
  editBriefAnswer: (key: PrefKey) => void;
}

const emptyPrefs = emptyPreferences();

/** Re-ask copy for the edit affordance (short forms of the brief prompts). */
const PREF_PROMPTS_EDIT: Partial<Record<PrefKey, string>> = {
  purpose: 'What brings you here?',
  goal: "What's the money's job here?",
  horizon: 'How long should the money stay in?',
  budget: "What's the ceiling you'd rather stay under?",
  propertyType: 'What kind of home are you picturing?',
  bhk: 'How much space does your family need?',
  plotSize: 'What size of land are you thinking?',
  location: 'Which side of the city?',
  worries: 'What quietly worries you about this purchase?',
  schools: 'Should I weigh school access?',
  hub: 'Where do you commute to?',
};

const BRIEF_CHIP_KEYS: PrefKey[] = ['purpose', 'goal', 'budget', 'bhk', 'propertyType', 'location'];

function isRecommendGoal(kind?: string): boolean {
  return kind === 'recommend' || kind === 'ack_reject_recommend';
}

/** Build the "checked" evidence card from what THIS turn's payload actually
 *  contained. Every item must be backed by a field in the response — no
 *  canned tool lists, no claims the pipeline didn't earn. */
function workNoteFromTurn(
  result: {
    projects?: AdvisorProjectCard[];
    focusedProject?: AdvisorProjectDetail;
    compareMatrix?: CompareMatrixPayload;
    visitItinerary?: VisitItineraryPayload;
  },
  showCatalog: boolean,
): WorkNote | null {
  const items: Array<{ k: string; v: string }> = [];
  if (showCatalog && result.projects?.length) {
    const areas = [...new Set(result.projects.map((p) => p.micro_market).filter(Boolean))] as string[];
    items.push({
      k: 'Catalog searched',
      v:
        `${result.projects.length} match${result.projects.length === 1 ? '' : 'es'}` +
        (areas.length ? ` · ${areas.slice(0, 2).join(', ')}` : ''),
    });
  }
  const fp = result.focusedProject;
  if (fp) {
    const parts: string[] = [];
    if (fp.rera_number || fp.khata || fp.ec_status || fp.na_status) parts.push('legal rows');
    if (fp.configurations?.length) parts.push(`${fp.configurations.length} unit type${fp.configurations.length === 1 ? '' : 's'}`);
    if (fp.starting_price_display) parts.push('price');
    if (fp.market_intel) parts.push('area intel');
    if (parts.length) items.push({ k: `${fp.name} pack`, v: parts.join(' · ') });
  }
  if (result.compareMatrix && result.compareMatrix.projects.length >= 2) {
    items.push({
      k: 'Compared',
      v: `${result.compareMatrix.projects.length} projects · ${result.compareMatrix.rows.length} rows`,
    });
  }
  if (result.visitItinerary?.stops?.length) {
    const n = result.visitItinerary.stops.length;
    items.push({ k: 'Visit route', v: `${n} stop${n === 1 ? '' : 's'} sequenced` });
  }
  return items.length > 0 ? { items } : null;
}

function mergeCatalogProjects(
  current: PrototypeProjectDetail[],
  incoming: Project[],
): PrototypeProjectDetail[] {
  if (incoming.length === 0) return current;
  const enriched = enrichProjects(incoming);
  const byId = new Map(current.map((p) => [p.id, p]));
  for (const p of enriched) {
    byId.set(p.id, { ...byId.get(p.id), ...p });
  }
  const ordered = enriched.map((p) => byId.get(p.id)!);
  for (const p of current) {
    if (!ordered.some((o) => o.id === p.id)) ordered.push(p);
  }
  return ordered;
}

function catalogFromMessages(messages: ChatMessage[]): PrototypeProjectDetail[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const p = messages[i]?.projects;
    if (p?.length) return enrichProjects(p);
  }
  return [];
}

function normalizePrefs(raw: Preferences): Preferences {
  return {
    ...emptyPreferences(),
    ...raw,
    bhkSelections: raw.bhkSelections?.length
      ? raw.bhkSelections
      : raw.bhk
        ? raw.bhk.split(' · ').filter(Boolean)
        : [],
    propertyTypes: raw.propertyTypes ?? [],
    plotSizeSelections: raw.plotSizeSelections ?? [],
    // Sessions persisted before the worries step lack the fields.
    worries: raw.worries ?? [],
    // Migration: old sessions carried worriesConfirmed; selections that exist
    // in a persisted session were all explicitly confirmed at the time.
    multiConfirmed:
      (raw as { multiConfirmed?: PrefKey[] }).multiConfirmed ??
      ([
        ...(raw.propertyTypes?.length ? ['propertyType' as const] : []),
        ...(raw.bhkSelections?.length ? ['bhk' as const] : []),
        ...(raw.plotSizeSelections?.length ? ['plotSize' as const] : []),
        ...((raw as { worriesConfirmed?: boolean }).worriesConfirmed || raw.worries?.length ? ['worries' as const] : []),
      ]),
  };
}

const EMPTY_DRAWER: DrawerState = { kind: null };

const DEFAULT_BOARD: BoardState = {
  view: 'welcome',
  projectId: '',
  tab: 'legal',
  floor: 8,
  unitNo: null,
};

const BEGIN_MESSAGES: ChatMessage[] = [
  {
    id: 'begin-1',
    role: 'bot',
    text: "Hi — I'm Naya. I won't drown you in filters; let's just talk, so I understand what this home needs to do for your family. Nothing you tell me reaches a builder until you decide to visit one.",
  },
  {
    id: 'begin-2',
    role: 'bot',
    text: 'Before I show you a single home — what brings you here?',
    quickReplies: (BRIEF_OPTIONS.purpose ?? []).map((value) => ({ pref: 'purpose', value })),
  },
];

const AdvisorContext = createContext<AdvisorState | null>(null);

function hasRecommendations(messages: ChatMessage[]): boolean {
  return messages.some((m) => (m.projects?.length ?? 0) > 0);
}

const CARD_ACCENTS = ['#3d4a6a', '#2d5a4a', '#5a3d4a', '#4a3d5a'];

function cardToProject(card: AdvisorProjectCard, prefs: Preferences, index: number): Project {
  return {
    id: card.id,
    name: card.name,
    builder: card.source_builder_id ?? 'Naya',
    location: card.micro_market ?? prefs.location ?? '',
    price: card.price_label ?? card.starting_price_display ?? '',
    bhk: prefs.bhk ?? '',
    // Live trade-off narration from the Desk preference re-rank. A note with a
    // real cost (⚠) renders as a stretch; all-gains renders as success.
    fit: card.tradeoff_note ?? '',
    fitTone: card.tradeoff_note ? (card.tradeoff_note.includes('⚠') ? 'stretch' : 'success') : 'neutral',
    accent: CARD_ACCENTS[index % CARD_ACCENTS.length]!,
  };
}

/** One-line ack of the fields the Spine newly understood from free text. Null
 *  when nothing new was captured (the caller then parks a question or stays
 *  silent — never the old "finish with the chips" canned line). */
function briefExtractAck(
  before: Preferences,
  after: Preferences,
): string | null {
  const parts: string[] = [];
  if (!before.budget && after.budget) parts.push(after.budget);
  if (before.bhkSelections.length === 0 && after.bhkSelections.length) {
    parts.push(after.bhkSelections.join(', '));
  }
  if (before.propertyTypes.length === 0 && after.propertyTypes.length) {
    parts.push(after.propertyTypes.join(', '));
  }
  if (!before.location && after.location) parts.push(after.location);
  if (!before.purpose && after.purpose) {
    parts.push(after.purpose === 'Investment' ? 'investment' : 'self-use');
  }
  if (parts.length === 0) return null;
  return `Got it — ${parts.join(' · ')}.`;
}

/** A buyer aside that reads like a question, not a brief answer — parked, not
 *  discarded, so the honest reply lands once the brief is done. */
function isQuestionShaped(text: string): boolean {
  const t = text.trim();
  return /\?\s*$/.test(t) || /^(?:what|how|which|why|when|where|can|could|do|does|is|are|should)\b/i.test(t);
}

/** Upper bound (₹) of a budget-band chip label, parsed generically from the
 *  label itself — no hardcoded boundaries. "₹1 Cr+" is open (Infinity). */
function bandUpperInr(label: string): number {
  if (label.includes('+')) return Number.POSITIVE_INFINITY;
  const parts = label.split(/[–—-]/);
  const upper = parts[parts.length - 1] ?? label;
  const m = upper.match(/(\d+(?:\.\d+)?)\s*(l|lakh|lac|cr|crore)/i);
  if (!m) return Number.POSITIVE_INFINITY;
  const n = Number.parseFloat(m[1]!);
  return /^c/i.test(m[2]!) ? n * 1e7 : n * 1e5;
}

/** Map a numeric budget to the first band whose range contains it, using the
 *  SPA's own band chips (so a new band set works with zero code change). */
function budgetBandForInr(inr: number, bands: readonly string[]): string | null {
  for (const b of bands) {
    if (inr <= bandUpperInr(b)) return b;
  }
  return bands[bands.length - 1] ?? null;
}

/** Find the chip label whose leading integer matches the snapshot's config. */
function matchConfigChip(value: string, chips: readonly string[]): string | null {
  const digit = value.match(/\d+/)?.[0];
  if (!digit) return null;
  return chips.find((c) => c.match(/\d+/)?.[0] === digit) ?? null;
}

function markConfirmed(prefs: Preferences, key: PrefKey): PrefKey[] {
  return prefs.multiConfirmed.includes(key) ? prefs.multiConfirmed : [...prefs.multiConfirmed, key];
}

/**
 * Merge the Spine's prefs_snapshot into the SPA brief. FILL-ONLY: a value the
 * buyer already set via chips is never overwritten (the F5 band mutation was an
 * unconditional budget write here). Free text that reached the Spine (brief
 * extract) pre-fills chips it maps to — including the multi-select steps —
 * so the ladder skips what the buyer already told us. Labels resolve against
 * `briefOptions`, never hardcoded.
 */
function mergePrefsFromSnapshot(
  current: Preferences,
  snapshot: Record<string, string | undefined> | undefined,
  briefOptions: BriefOptionsMap = BRIEF_OPTIONS,
): Preferences {
  if (!snapshot) return current;
  const next = { ...current };

  if (snapshot.location && !current.location) next.location = snapshot.location;

  if (!current.budget) {
    const bands = briefOptions.budget ?? BRIEF_OPTIONS.budget ?? [];
    const inr = snapshot.budget_max_inr ? Number.parseInt(snapshot.budget_max_inr, 10) : NaN;
    const band = Number.isFinite(inr) ? budgetBandForInr(inr, bands) : null;
    // Prefer the numeric→band map; fall back to the display band only if it is
    // itself one of our chips (never store a raw "₹80 L" that no chip matches).
    if (band) next.budget = band;
    else if (snapshot.budget && bands.includes(snapshot.budget)) next.budget = snapshot.budget;
  }

  if (snapshot.purpose && !current.purpose) {
    next.purpose = snapshot.purpose === 'investment' ? 'Investment' : 'Self-use';
  }

  if (snapshot.bhk && current.bhkSelections.length === 0) {
    const chip = matchConfigChip(snapshot.bhk, briefOptions.bhk ?? []);
    if (chip) {
      next.bhkSelections = [chip];
      next.bhk = chip;
      next.multiConfirmed = markConfirmed(next, 'bhk');
    }
  }

  if (snapshot.property_type && current.propertyTypes.length === 0) {
    const wanted = snapshot.property_type.toLowerCase();
    const chips = briefOptions.propertyType ?? [];
    const matched = chips.filter((c) => wanted.includes(c.toLowerCase().split(' ')[0]!));
    if (matched.length) {
      next.propertyTypes = matched;
      next.multiConfirmed = markConfirmed(next, 'propertyType');
    }
  }

  return next;
}

function hydrateInitial(): {
  sessionId: string;
  messages: ChatMessage[];
  prefs: Preferences;
  shortlist: Project[];
  unitShortlist: ShortlistedUnit[];
  engagedProjectIds: string[];
  bookedVisits: BookedVisit[];
} {
  const sessionId = getOrCreateSessionId();
  const saved = loadPersistedState();
  if (saved && saved.sessionId === sessionId) {
    const engagedProjectIds =
      saved.engagedProjectIds ??
      saved.shortlist.map((p) => p.id);
    return {
      sessionId,
      messages: saved.messages.length > 0 ? saved.messages : INITIAL_MESSAGES,
      prefs: normalizePrefs(saved.prefs),
      shortlist: saved.shortlist,
      unitShortlist: saved.unitShortlist ?? [],
      engagedProjectIds,
      bookedVisits: saved.bookedVisits ?? [],
    };
  }
  return {
    sessionId,
    messages: INITIAL_MESSAGES,
    prefs: emptyPrefs,
    shortlist: [],
    unitShortlist: [],
    engagedProjectIds: [],
    bookedVisits: [],
  };
}

export function AdvisorProvider({ children }: { children: React.ReactNode }) {
  const initial = useRef(hydrateInitial()).current;
  const initialCatalog = catalogFromMessages(initial.messages);
  const mode = getAdvisorMode();
  const briefDispatchKeyRef = useRef<string | null>(null);
  const catalogPoolRef = useRef<PrototypeProjectDetail[]>(initialCatalog);
  const serverPhaseRef = useRef<string>('discover');

  const [sessionId, setSessionId] = useState(initial.sessionId);
  const [appView, setAppView] = useState<AppView>(
    initial.messages.some((m) => (m.projects?.length ?? 0) > 0) || initial.prefs.purpose
      ? 'app'
      : 'home',
  );
  const [board, setBoard] = useState<BoardState>(DEFAULT_BOARD);
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [visitDate, setVisitDateState] = useState<string | null>(null);
  const [visitSkipped, setVisitSkipped] = useState<Record<string, boolean>>({});
  const [engagedProjectIds, setEngagedProjectIds] = useState<string[]>(initial.engagedProjectIds);
  const [bookedVisits, setBookedVisits] = useState<BookedVisit[]>(initial.bookedVisits);
  const [visitQueue, setVisitQueue] = useState<VisitQueuePayload | null>(null);
  const [visitItinerary, setVisitItinerary] = useState<VisitItineraryPayload | null>(null);
  const [emi, setEmi] = useState({ down: 20, rate: 8.5, tenure: 20 });
  /** True once the buyer actually worked the EMI numbers — lights the
   *  roadmap lane; opening the app alone must not. */
  const [emiTouched, setEmiTouched] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(initial.messages);
  const [prefs, setPrefs] = useState<Preferences>(() => normalizePrefs(initial.prefs));
  const [catalogProjects, setCatalogProjects] = useState<PrototypeProjectDetail[]>(initialCatalog);
  const [shortlist, setShortlist] = useState<Project[]>(initial.shortlist);
  const [unitShortlist, setUnitShortlist] = useState<ShortlistedUnit[]>(initial.unitShortlist);
  const [isLoading, setIsLoading] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState>(EMPTY_DRAWER);
  const [boardSheetOpen, setBoardSheetOpen] = useState(false);
  const [briefOptions, setBriefOptions] = useState<BriefOptionsMap>(() => buildBriefOptions());
  const [projectDetails, setProjectDetails] = useState<Record<string, AdvisorProjectDetail>>({});
  const [projectDetailLoading, setProjectDetailLoading] = useState<string | null>(null);
  const [compareMatrix, setCompareMatrix] = useState<CompareMatrixPayload | null>(null);
  const [uiMode, setUiMode] = useState<AdvisorUiMode>('brief_collect');
  const [lastSearchRecovery, setLastSearchRecovery] = useState<SearchRecoveryPayload | null>(null);
  const [lastNbaChips, setLastNbaChips] = useState<string[] | null>(null);
  // ADR-006: chip label → action_id, so a content-chip tap sends action_id and
  // the door skips the LLM extract. Rails/omitted → tap sends free text as before.
  const [lastNbaChipActions, setLastNbaChipActions] = useState<Record<string, string> | null>(null);
  // Raw server prefs_snapshot — the "what Naya understands" tray renders this
  // verbatim (client `prefs` is deliberately lossy; see mergePrefsFromSnapshot).
  const [signalSnapshot, setSignalSnapshot] = useState<Record<string, string | undefined> | null>(null);
  // Real catalog size (brief-facets project_count) — the honest start anchor for
  // the shortlisting count; 0 until loaded. `shortlisting` is true while a
  // brief→results dispatch is in flight (drives the thinking strip, not the
  // plain typing dots).
  const [catalogCount, setCatalogCount] = useState(0);
  const [shortlisting, setShortlisting] = useState(false);

  const briefReady = isBriefReady(prefs, briefOptions);
  const hasCatalog = catalogProjects.length > 0;
  const preferenceEditingActive =
    uiMode === 'search_recovery' || uiMode === 'preference_refine';
  const briefPhase: 'collecting' | 'matched' | 'focused' =
    uiMode === 'focused' || board.view === 'project'
      ? 'focused'
      : uiMode === 'brief_collect' && !hasCatalog
        ? 'collecting'
        : 'matched';

  const openCompareBoard = useCallback(
    (matrix?: CompareMatrixPayload | null) => {
      const next =
        matrix ??
        buildCompareMatrixFromProjects(catalogProjects.slice(0, 3)) ??
        null;
      setCompareMatrix(next);
      setDrawer(EMPTY_DRAWER);
      setBoard((b) => ({ ...b, view: 'compare' }));
    },
    [catalogProjects],
  );
  // Multi chips follow nextBriefKey only — do not gate on hasCatalog (that
  // killed propertyType / worries mid-brief once any catalog leaked in) or on
  // preferenceEditingActive (refine uses its own chip path separately).
  const activeMultiPref = (() => {
    if (preferenceEditingActive) return null;
    const key = nextBriefKey(prefs, briefOptions);
    return key && isMultiBriefKey(key) ? key : null;
  })();

  const matchProjects = catalogProjects;

  const engagedProjects = useMemo(
    () =>
      engagedProjectIds
        .map((id) => matchProjects.find((p) => p.id === id))
        .filter((p): p is PrototypeProjectDetail => !!p),
    [engagedProjectIds, matchProjects],
  );

  const visitRouteStopIds = useMemo(
    () =>
      buildVisitRouteStopIds({
        bookedVisits,
        visitQueue,
        visitItinerary,
        engagedProjectIds,
        visitSkipped,
        mode,
      }),
    [bookedVisits, visitQueue, visitItinerary, engagedProjectIds, visitSkipped, mode],
  );

  const visitAwaitingConfirm = visitQueue?.awaiting_confirm === true;

  const focusedProject = useMemo(() => {
    if (!board.projectId) return null;
    return matchProjects.find((p) => p.id === board.projectId) ?? null;
  }, [board.projectId, matchProjects]);

  const markEngaged = useCallback(
    (projectId: string) => {
      const project = matchProjects.find((p) => p.id === projectId);
      if (!project) return;
      setEngagedProjectIds((prev) => (prev.includes(projectId) ? prev : [...prev, projectId]));
      setShortlist((list) => (list.some((p) => p.id === projectId) ? list : [...list, project]));
    },
    [matchProjects],
  );

  useEffect(() => {
    if (matchProjects.length > 0 && board.view === 'welcome') {
      setBoard((b) => ({
        ...b,
        view: 'matches',
        projectId: b.projectId || matchProjects[0]!.id,
      }));
    }
  }, [matchProjects.length, board.view]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kind = params.get('x') as ToolKind | null;
    const projectId = params.get('p') ?? undefined;
    if (kind === 'floor_plan' && projectId) {
      setDrawer({ kind: 'plans', projectId, unitId: null });
    }
  }, []);

  useEffect(() => {
    savePersistedState({
      sessionId,
      messages,
      prefs,
      shortlist,
      unitShortlist,
      engagedProjectIds,
      bookedVisits,
    });
  }, [sessionId, messages, prefs, shortlist, unitShortlist, engagedProjectIds, bookedVisits]);

  useEffect(() => {
    if (mode !== 'live') return;
    const controller = new AbortController();
    void fetchAdvisorBriefFacets(undefined, controller.signal).then((resp) => {
      if (resp.status !== 'ok') return;
      if (typeof resp.project_count === 'number') setCatalogCount(resp.project_count);
      setBriefOptions(
        buildBriefOptions({
          locations: resp.locations ?? [],
          bhk: resp.bhk ?? [],
          property_types: resp.property_types ?? [],
          plot_sizes: resp.plot_sizes ?? [],
        }),
      );
    });
    return () => controller.abort();
  }, [mode, sessionId]);

  const applyLiveDetail = useCallback((projectId: string, detail: AdvisorProjectDetail) => {
    setProjectDetails((prev) => ({ ...prev, [projectId]: detail }));
  }, []);

  const syncVisitBooked = useCallback(
    (visit: BookedVisit) => {
      const project = projectFromBookedVisit(visit, catalogProjects);
      setBookedVisits((prev) => [...prev.filter((v) => v.projectId !== visit.projectId), visit]);
      setShortlist((prev) => (prev.some((p) => p.id === visit.projectId) ? prev : [...prev, project]));
      setVisitSkipped((prev) => ({ ...prev, [visit.projectId]: false }));
      setBoard((b) => ({ ...b, projectId: visit.projectId }));
    },
    [catalogProjects],
  );

  const loadProjectDetail = useCallback(
    async (projectId: string, projectName?: string) => {
      if (mode !== 'live') return;
      setProjectDetailLoading(projectId);
      try {
        const result = await fetchAdvisorProjectDetail(sessionId, projectId);
        if (result.status === 'ok' && result.project) {
          applyLiveDetail(projectId, result.project);
          return;
        }
        if (projectName) {
          void sendAdvisorTurn({
            sessionId,
            text: `Tell me about ${projectName}`,
            projectId,
            projectName,
          }).then((turn) => {
            if (turn.focusedProject) applyLiveDetail(projectId, turn.focusedProject);
          });
        }
      } finally {
        setProjectDetailLoading((current) => (current === projectId ? null : current));
      }
    },
    [mode, sessionId, applyLiveDetail],
  );

  const getBoardProject = useCallback(
    (projectId: string): PrototypeProjectDetail => {
      const base =
        catalogProjects.find((p) => p.id === projectId) ??
        enrichProjects([
          {
            id: projectId,
            name: projectId,
            builder: '—',
            location: '—',
            price: '—',
            bhk: '—',
            fit: '',
            accent: '#9a7734',
          },
        ])[0]!;
      return projectWithLiveDetail(base, projectDetails[projectId]);
    },
    [catalogProjects, projectDetails],
  );

  const appendBotMessages = useCallback((extra: ChatMessage[]) => {
    if (extra.length === 0) return;
    setMessages((m) => [...m, ...extra]);
  }, []);

  const showBoardProject = useCallback(
    (projectId: string, tab: ProjectTab = 'legal', opts?: { silent?: boolean }) => {
      const name = matchProjects.find((p) => p.id === projectId)?.name ?? projectId;
      markEngaged(projectId);
      setBoard((b) => ({
        ...b,
        view: 'project',
        projectId,
        tab,
        unitNo: null,
      }));
      if (mode === 'live') {
        void loadProjectDetail(projectId, name);
      }
      if (!opts?.silent) {
        setMessages((m) => {
          const last = m[m.length - 1];
          if (last?.role === 'bot' && last.text?.startsWith('Opened ') && last.text.includes(name)) {
            return m;
          }
          return [
            ...m,
            {
              id: nextId(),
              role: 'bot',
              text: `Opened ${name} on the board — ${tab === 'legal' ? 'legal & title' : tab} tab.`,
            },
          ];
        });
      }
    },
    [matchProjects, mode, loadProjectDetail, markEngaged],
  );

  const applyBoardRoute = useCallback(
    (
      result: {
        goalKind?: string;
        goalTopic?: string;
        goalProjectId?: string;
        phase?: string;
        visitBooked?: { projectId: string };
        compareMatrix?: CompareMatrixPayload;
        focusedProject?: AdvisorProjectDetail;
        nba?: AdvisorNba;
        checklistSnapshot?: AdvisorChecklistSnapshot;
      },
      userText: string,
    ) => {
      if (result.checklistSnapshot?.engaged_project_ids?.length) {
        for (const id of result.checklistSnapshot.engaged_project_ids) {
          markEngaged(id);
        }
      }

      const route = result.nba
        ? boardRouteFromNba(result.nba)
        : boardRouteAction({
            userText,
            goalKind: result.goalKind,
            goalTopic: result.goalTopic,
            goalProjectId: result.goalProjectId,
            phase: result.phase,
            visitBooked: !!result.visitBooked,
            projects: matchProjects.map((p) => ({ id: p.id, name: p.name })),
          });

      switch (route.action) {
        case 'compare':
          openCompareBoard(
            result.compareMatrix ?? buildCompareMatrixFromProjects(matchProjects.slice(0, 3)),
          );
          break;
        case 'matches':
          // POI chips ("Schools near my matches") must not wipe project focus —
          // that made Eldorado appear only when free text named it.
          if (isFocusedPoiAsk(userText)) break;
          setBoard((b) => ({
            ...b,
            view: 'matches',
            projectId: '',
          }));
          break;
        case 'visit':
          setBoard((b) => ({ ...b, view: 'visit' }));
          break;
        case 'project': {
          const detail =
            result.focusedProject?.project_id === route.projectId
              ? result.focusedProject
              : undefined;
          if (detail) applyLiveDetail(route.projectId, detail);
          markEngaged(route.projectId);
          showBoardProject(route.projectId, route.tab, { silent: true });
          break;
        }
        case 'none':
          break;
      }
    },
    [matchProjects, openCompareBoard, showBoardProject, applyLiveDetail, markEngaged],
  );

  const dispatchLiveTurn = useCallback(
    async (
      userText: string,
      focus?: { projectId: string; projectName: string },
      preferenceOverride?: Preferences,
      turnOpts?: { boardVisitMode?: boolean; skipBotMessage?: boolean; matchesHub?: boolean; actionId?: string },
    ) => {
      setIsLoading(true);
      const prefsForTurn = preferenceOverride ?? prefs;
      const inRecoveryTurn =
        uiMode === 'search_recovery' ||
        uiMode === 'preference_refine' ||
        Boolean(turnOpts?.actionId);
      const sendPrefs =
        !inRecoveryTurn && isBriefReady(prefsForTurn, briefOptions)
          ? prefsToAdvisorPreferences(prefsForTurn)
          : undefined;
      const matchesHubTurn =
        turnOpts?.matchesHub === true ||
        shouldClearMatchFocus(userText, briefOptions.location ?? []);
      const boardVisitActive =
        !matchesHubTurn && (turnOpts?.boardVisitMode ?? board.view === 'visit');
      const routeExpandOnBoard = boardVisitActive && isVisitRouteExpandTurn(userText);
      const schedulingOnBoard =
        boardVisitActive && (isVisitBoardSchedulingTurn(userText) || routeExpandOnBoard);
      try {
        const result = await sendAdvisorTurn({
          sessionId,
          text: userText,
          projectId:
            schedulingOnBoard ||
            serverPhaseRef.current === 'visit' ||
            isGlobalAdvisorTurn(userText)
              ? undefined
              : focus?.projectId,
          projectName:
            schedulingOnBoard ||
            serverPhaseRef.current === 'visit' ||
            isGlobalAdvisorTurn(userText)
              ? undefined
              : focus?.projectName,
          preferences: sendPrefs,
          actionId: turnOpts?.actionId,
          boardTab: board.view === 'project' ? board.tab : board.view,
        });
        if (result.status === 'deduplicated') {
          return;
        }
        if (result.status === 'ok' && result.reply) {
          if (result.phase) serverPhaseRef.current = result.phase;
          if (result.nba?.chips?.length) {
            setLastNbaChips(result.nba.chips);
            // Zip chips ↔ chip_actions into a label→action_id map (empty entries
            // dropped) so a facet-chip tap can go deterministic.
            const actions = result.nba.chip_actions;
            if (actions?.length) {
              const map: Record<string, string> = {};
              result.nba.chips.forEach((label, i) => {
                const a = actions[i];
                if (a) map[label] = a;
              });
              setLastNbaChipActions(Object.keys(map).length ? map : null);
            } else {
              setLastNbaChipActions(null);
            }
          } else {
            setLastNbaChips(null);
            setLastNbaChipActions(null);
          }
          if (result.visitQueue) {
            setVisitQueue(result.visitQueue);
          } else if (result.phase !== 'visit') {
            setVisitQueue(null);
          }
          if (result.visitItinerary) {
            setVisitItinerary(result.visitItinerary);
          } else if (result.phase !== 'visit') {
            setVisitItinerary(null);
          }
          if (result.prefsSnapshot) {
            setPrefs((current) => mergePrefsFromSnapshot(current, result.prefsSnapshot, briefOptions));
            setSignalSnapshot(result.prefsSnapshot);
          }
          if (result.uiMode) setUiMode(result.uiMode);
          if (result.searchRecovery) {
            setLastSearchRecovery(result.searchRecovery);
          } else if (
            result.uiMode !== 'search_recovery' &&
            result.uiMode !== 'preference_refine'
          ) {
            setLastSearchRecovery(null);
          }
          if (result.focusedProject) {
            applyLiveDetail(result.focusedProject.project_id, result.focusedProject);
          }
          const cardPrefs: Preferences = { ...prefs };
          const projects = result.projects?.map((card, i) => cardToProject(card, cardPrefs, i));
          const showCatalog = isRecommendGoal(result.goalKind) && (projects?.length ?? 0) > 0;

          if (projects?.length) {
            catalogPoolRef.current = mergeCatalogProjects(catalogPoolRef.current, projects);
            setCatalogProjects((prev) =>
              showCatalog ? enrichProjects(projects) : mergeCatalogProjects(prev, projects),
            );
          }

          if (result.visitBooked) {
            syncVisitBooked(result.visitBooked);
          }

          const routeInput = {
            userText,
            goalKind: result.goalKind,
            goalTopic: result.goalTopic,
            goalProjectId: result.goalProjectId,
            phase: result.phase,
            visitBooked: !!result.visitBooked,
            projects: matchProjects.map((p) => ({ id: p.id, name: p.name })),
          };
          const route = boardRouteAction(routeInput);
          applyBoardRoute(result, userText);

          if (matchesHubTurn) {
            // Keep sticky project for POI facet asks (schools/commute chips).
            if (!isFocusedPoiAsk(userText)) {
              setBoard((b) => ({ ...b, view: 'matches', projectId: '' }));
            }
          }

          if (showCatalog && projects?.length && route.action !== 'project') {
            setBoard((b) => ({
              ...b,
              view: 'matches',
              projectId: shouldClearMatchFocus(userText, briefOptions.location ?? [])
                ? ''
                : projects[0]!.id,
            }));
          }

          const turnFocus = resolveTurnFocus(
            userText,
            projectRefsFromCatalog(matchProjects),
            focus?.projectId ?? board.projectId,
          );
          if (
            turnFocus &&
            !matchesHubTurn &&
            !isGlobalAdvisorTurn(userText) &&
            !(boardVisitActive && isVisitBoardSchedulingTurn(userText))
          ) {
            markEngaged(turnFocus.projectId);
            if (!boardVisitActive) {
              setBoard((b) => ({
                ...b,
                projectId: turnFocus.projectId,
              }));
            }
          }

          const isCompareTurn =
            result.goalTopic === 'compare' || /\bcompare\b/i.test(userText);
          let botText = isCompareTurn
            ? chatReplyForCompare(result.reply!)
            : result.reply!;
          if (
            mode !== 'live' &&
            boardVisitActive &&
            isVisitSchedulingGoal(result.goalKind) &&
            route.action === 'none'
          ) {
            botText = chatReplyForBoardVisit(result.reply!, result.goalKind, mode);
          } else if (
            mode !== 'live' &&
            boardVisitActive &&
            schedulingOnBoard &&
            !isVisitSchedulingGoal(result.goalKind) &&
            result.goalKind !== 'visit_booked'
          ) {
            const dateLabel = VISIT_DATES.find((d) => d.id === visitDate)?.label;
            botText = visitBoardSchedulingReply(userText, dateLabel);
          }

          // Demo-only. The buyer asked to widen, and the local fixture has no
          // widened catalog to show — so offer refine. Live turns never reach
          // this: the engine reports suggest_refine itself, and we no longer
          // inspect the reply's prose to work it out.
          const wantsWider =
            mode !== 'live' &&
            !showCatalog &&
            (isExploreMoreTurn(userText) || isLocationExpandTurn(userText, briefOptions.location ?? []));
          const isRefineTurn = isSearchRefinementUserLine(userText);
          let refineFiltered: PrototypeProjectDetail[] | null = null;

          // Live turns: trust ConverseSpine reply + project cards. Local catalog filter is demo-only.
          if (mode !== 'live' && isRefineTurn && !showCatalog) {
            refineFiltered = filterCatalogByPrefs(catalogPoolRef.current, prefsForTurn);
            setCatalogProjects(refineFiltered);
            if (refineFiltered.length > 0) {
              botText = refineSearchAck(prefsForTurn, refineFiltered.length);
            } else {
              botText = exploreMoreRefineReply(prefsForTurn);
            }
          } else if (wantsWider) {
            botText = exploreMoreRefineReply(prefsForTurn);
          }
          // Demo stand-in for what the engine now reports directly.
          const stalePitch = wantsWider && !isRefineTurn;

          // Live: the engine states it. Demo: derived from the local filter.
          // Neither reads the reply text.
          const suggestRefineFlag =
            result.suggestRefine === true ||
            (refineFiltered !== null && refineFiltered.length <= 1) ||
            stalePitch ||
            result.goalKind === 'ack_reject_recommend' ||
            !!result.searchRecovery?.suggested_actions?.length;

          if (!turnOpts?.skipBotMessage) {
            const work = workNoteFromTurn(result, showCatalog);
            appendBotMessages([
              {
                id: nextId(),
                role: 'bot',
                text: botText,
                suggestRefine: suggestRefineFlag,
                ...(work ? { work } : {}),
                ...(result.searchRecovery?.suggested_actions?.length
                  ? { suggestedActions: result.searchRecovery.suggested_actions }
                  : {}),
                ...(result.visitBooked
                  ? {
                      visitConfirmed: {
                        project: result.visitBooked.projectName,
                        time: result.visitBooked.label,
                      },
                    }
                  : {}),
                ...(showCatalog && projects?.length
                  ? {
                      projects,
                      showVisitPlan: projects.length >= 2,
                    }
                  : {}),
              },
            ]);
          }
        } else if (result.status === 'error') {
          // User-facing copy only — never leak internal config (Cowork N2: the
          // old message exposed "set VITE_ADVISOR_MODE=demo in .env").
          const msg =
            result.error === 'timeout'
              ? "That took longer than usual to load — give it another try in a moment."
              : "I'm having trouble reaching our data right now — please try again in a moment.";
          appendBotMessages([{ id: nextId(), role: 'bot', text: msg }]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, appendBotMessages, prefs, briefOptions, uiMode, applyLiveDetail, board.view, board.projectId, visitDate, syncVisitBooked, applyBoardRoute, matchProjects, markEngaged],
  );

  const scheduleBriefDispatch = useCallback(
    (text: string, overridePrefs: Preferences) => {
      const key = `${sessionId}:${text}`;
      if (briefDispatchKeyRef.current === key) return;
      briefDispatchKeyRef.current = key;
      // This is the brief→results moment — the slow one. Show the shortlisting
      // strip (narrowing) instead of the plain typing dots until it settles.
      setShortlisting(true);
      void dispatchLiveTurn(text, undefined, overridePrefs).finally(() => {
        if (briefDispatchKeyRef.current === key) briefDispatchKeyRef.current = null;
        setShortlisting(false);
      });
    },
    [sessionId, dispatchLiveTurn],
  );

  /**
   * Brief-phase free text → the Spine's extraction (the one language authority),
   * WITHOUT searching. Constraints come back via prefs_snapshot and pre-fill the
   * chips; the ladder then skips what the buyer already told us. If the message
   * completes the brief, the single real dispatch fires here — so results still
   * appear only once brief-ready, whichever lane filled it. Non-blocking: the
   * chip ladder keeps rendering while this resolves.
   */
  const dispatchBriefExtract = useCallback(
    async (text: string) => {
      const result = await sendAdvisorTurn({ sessionId, text, briefExtract: true });
      if (result.status !== 'ok' || !result.prefsSnapshot) return;
      const snap = result.prefsSnapshot;
      const before = prefs;
      const after = mergePrefsFromSnapshot(before, snap, briefOptions);
      setPrefs((current) => mergePrefsFromSnapshot(current, snap, briefOptions));
      setSignalSnapshot(snap);

      const ack = briefExtractAck(before, after);
      if (ack) {
        setMessages((m) => [...m, { id: nextId(), role: 'bot', text: ack }]);
      } else if (isQuestionShaped(text)) {
        setMessages((m) => [
          ...m,
          {
            id: nextId(),
            role: 'bot',
            text: "Good question — I'll come back to that once your brief is done.",
          },
        ]);
      }

      if (isBriefReady(after, briefOptions) && !isDeepeningPending(after, briefOptions)) {
        scheduleBriefDispatch(formatBriefForBot(after), after);
      }
    },
    [sessionId, prefs, briefOptions, scheduleBriefDispatch],
  );

  const toggleMultiPref = useCallback((pref: MultiBriefPref, value: string) => {
    setPrefs((current) => toggleMultiPrefValue(current, pref, value));
  }, []);

  const confirmMultiPrefStep = useCallback(
    (pref: MultiBriefPref) => {
      setPrefs((current) => {
        const values =
          pref === 'bhk'
            ? current.bhkSelections
            : pref === 'plotSize'
              ? current.plotSizeSelections
              : pref === 'worries'
                ? current.worries
                : current.propertyTypes;
        if (values.length === 0) return current;

        const base = pref === 'propertyType' ? pruneConfigSelections(current) : current;
        const nextPrefs = base.multiConfirmed.includes(pref)
          ? base
          : { ...base, multiConfirmed: [...base.multiConfirmed, pref] };

        if (mode === 'live') {
          setMessages((m) => [...m, ...prefAckAfterMultiStep(pref, nextPrefs, briefOptions)]);
          if (isBriefReady(nextPrefs, briefOptions) && !isDeepeningPending(nextPrefs, briefOptions)) {
            scheduleBriefDispatch(formatBriefForBot(nextPrefs), nextPrefs);
          }
          return nextPrefs;
        }

        setMessages((m) => {
          const nextMsgs = [...m, ...prefAckAfterMultiStep(pref, nextPrefs, briefOptions)];
          if (isBriefReady(nextPrefs, briefOptions)) {
            return [...nextMsgs, ...recommendationMessages(nextPrefs.budget ?? '')];
          }
          return nextMsgs;
        });
        return nextPrefs;
      });
    },
    [mode, scheduleBriefDispatch, briefOptions],
  );

  const setPref = useCallback(
    (pref: PrefKey, value: string) => {
      setPrefs((current) => {
        const next = applyPref(current, pref, value);

        if (mode === 'live') {
          setMessages((m) => {
            const already = hasRecommendations(m) || catalogProjects.length > 0;
            if (already && BRIEF_CHIP_KEYS.includes(pref)) {
              return m;
            }
            if (already) {
              void dispatchLiveTurn(value);
              return [...m, { id: nextId(), role: 'user', text: value }];
            }
            if (isBriefReady(next, briefOptions) && !isDeepeningPending(next, briefOptions)) {
              scheduleBriefDispatch(formatBriefForBot(next), next);
              return [...m, { id: nextId(), role: 'user', text: value }];
            }
            return [...m, ...prefAckMessages(pref, value, next, briefOptions)];
          });
          return next;
        }

        setMessages((m) => {
          const already = hasRecommendations(m);
          if (already) return [...m, { id: nextId(), role: 'user', text: value }];
          return [...m, ...prefAckMessages(pref, value, next, briefOptions)];
        });
        return next;
      });
    },
    [mode, dispatchLiveTurn, scheduleBriefDispatch, catalogProjects.length, briefOptions],
  );

  /** Back/undo: reopen one brief answer pre-dispatch — clear it and re-ask. */
  const editBriefAnswer = useCallback(
    (key: PrefKey) => {
      setPrefs((current) => {
        const next: Preferences = { ...current };
        if (key === 'bhk') { next.bhkSelections = []; next.bhk = null; }
        else if (key === 'plotSize') next.plotSizeSelections = [];
        else if (key === 'propertyType') next.propertyTypes = [];
        else if (key === 'worries') next.worries = [];
        else (next as unknown as Record<string, unknown>)[key] = null;
        if (isMultiBriefKey(key)) {
          next.multiConfirmed = next.multiConfirmed.filter((k) => k !== key);
        }
        setMessages((m) => [
          ...m,
          {
            id: nextId(),
            role: 'bot',
            text: `Sure — let's revisit that. ${PREF_PROMPTS_EDIT[key] ?? ''}`.trim(),
            ...(isMultiBriefKey(key)
              ? {}
              : { quickReplies: (briefOptions[key] ?? []).map((v) => ({ pref: key, value: v })) }),
          },
        ]);
        return next;
      });
    },
    [briefOptions],
  );

  const showRecommendations = useCallback(() => {
    setMessages((m) => {
      if (hasRecommendations(m)) return m;
      if (!isBriefReady(prefs, briefOptions)) return m;
      return [...m, ...recommendationMessages(prefs.budget ?? '')];
    });
  }, [prefs, briefOptions]);

  /** Receipts are thread memory for closed sheets. Deduped against the
   *  recent tail so close/reopen/close doesn't stack identical lines. */
  const addReceipt = useCallback((receipt: ThreadReceipt) => {
    setMessages((m) => {
      const recent = m.slice(-8);
      const dup = recent.some(
        (msg) =>
          msg.receipt &&
          msg.receipt.kind === receipt.kind &&
          (msg.receipt.projectId ?? '') === (receipt.projectId ?? ''),
      );
      if (dup) return m;
      return [...m, { id: nextId(), role: 'bot' as const, receipt }];
    });
  }, []);

  const begin = useCallback(() => {
    setAppView('app');
    setBoard(DEFAULT_BOARD);
    if (messages.length <= INITIAL_MESSAGES.length) {
      setMessages(BEGIN_MESSAGES.map((m) => ({ ...m, id: nextId() })));
    }
  }, [messages.length]);

  const goHome = useCallback(() => setAppView('home'), []);

  const setBoardView = useCallback((view: BoardView) => {
    setBoard((b) => ({ ...b, view }));
  }, []);

  const showAllMatches = useCallback((opts?: { clearFocus?: boolean }) => {
    setBoard((b) => ({
      ...b,
      view: 'matches',
      projectId: opts?.clearFocus ? '' : b.projectId || catalogProjects[0]?.id || '',
    }));
  }, [catalogProjects]);

  const switchBoardFocus = useCallback((projectId: string) => {
    setBoard((b) => ({ ...b, view: 'matches', projectId }));
  }, []);

  const refineSearch = useCallback(
    (userLine: string, patch: (prefs: Preferences) => Preferences, actionId?: string) => {
      showAllMatches({ clearFocus: true });
      setMessages((m) => [...m, { id: nextId(), role: 'user', text: userLine }]);
      const next = patch(prefs);
      setPrefs(next);
      if (mode === 'live') {
        void dispatchLiveTurn(userLine, undefined, next, {
          boardVisitMode: false,
          matchesHub: true,
          actionId,
        });
        return;
      }
      const filtered = filterCatalogByPrefs(catalogPoolRef.current, next);
      setCatalogProjects(filtered);
      appendBotMessages([
        {
          id: nextId(),
          role: 'bot',
          text:
            filtered.length > 0
              ? refineSearchAck(next, filtered.length)
              : exploreMoreRefineReply(next),
          suggestRefine: filtered.length <= 1,
        },
      ]);
    },
    [showAllMatches, mode, dispatchLiveTurn, appendBotMessages, prefs],
  );

  const refineFromChip = useCallback(
    (label: string) => {
      const lastBot = [...messages].reverse().find((m) => m.role === 'bot');
      const actions = lastSearchRecovery?.suggested_actions ?? lastBot?.suggestedActions;
      const resolved = resolveSuggestedAction(label, actions, prefs, briefOptions);
      if (!resolved) return false;
      refineSearch(resolved.userLine, resolved.patch, resolved.actionId);
      return true;
    },
    [prefs, briefOptions, refineSearch, lastSearchRecovery, messages],
  );

  const exploreMoreProjects = useCallback((actionId?: string) => {
    const text = 'Show me other projects that fit my brief';
    showAllMatches({ clearFocus: true });
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    if (mode === 'live') {
      void dispatchLiveTurn(text, undefined, prefs, {
        boardVisitMode: false,
        matchesHub: true,
        ...(actionId ? { actionId } : {}),
      });
      return;
    }
    appendBotMessages([
      {
        id: nextId(),
        role: 'bot',
        text: exploreMoreRefineReply(prefs),
        suggestRefine: true,
      },
    ]);
  }, [mode, showAllMatches, dispatchLiveTurn, appendBotMessages, prefs]);

  /** Chip / nav: leave project focus and re-sync Spine to matches hub (not UI-only). */
  const returnToMatchesHub = useCallback(() => {
    const text = 'Back to my matches';
    showAllMatches({ clearFocus: true });
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    if (mode === 'live') {
      void dispatchLiveTurn(text, undefined, prefs, { boardVisitMode: false, matchesHub: true });
      return;
    }
    appendBotMessages([
      {
        id: nextId(),
        role: 'bot',
        text: 'Your matches are on the board →',
      },
    ]);
  }, [mode, showAllMatches, dispatchLiveTurn, appendBotMessages, prefs]);

  const beginBriefRefine = useCallback(
    (hint: 'area' | 'budget' | 'widen' | 'general' = 'general') => {
      showAllMatches({ clearFocus: true });
      setUiMode('preference_refine');
      setLastNbaChips(null);
      setLastNbaChipActions(null);
      setLastSearchRecovery(null);
      const text =
        hint === 'area'
          ? 'Want to change area? Tap a location chip or type a micro-market.'
          : hint === 'budget'
            ? 'Want to adjust budget? Tap a budget chip or tell me your new range.'
            : hint === 'widen'
              ? 'Let’s widen the search. Change area, budget, or configuration below.'
              : 'Refine your brief — change area, budget, or configuration, then I’ll re-match.';
      appendBotMessages([{ id: nextId(), role: 'bot', text, suggestRefine: true }]);
    },
    [showAllMatches, appendBotMessages],
  );

  const compareAllMatches = useCallback((actionId?: string) => {
    const n = matchProjects.length;
    if (n < 2) {
      appendBotMessages([
        {
          id: nextId(),
          role: 'bot',
          text: 'I need at least two matches to compare — ask for more options or open another project.',
        },
      ]);
      return;
    }
    const text = `Compare all ${n}`;
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    if (mode === 'live') {
      void dispatchLiveTurn(text, undefined, undefined, actionId ? { actionId } : undefined);
      return;
    }
    openCompareBoard();
    appendBotMessages([
      {
        id: nextId(),
        role: 'bot',
        text: 'Side-by-side comparison is on your board →',
      },
    ]);
  }, [mode, matchProjects.length, dispatchLiveTurn, appendBotMessages, openCompareBoard]);

  const openBoardProject = useCallback(
    (projectId: string, tab: ProjectTab = 'legal') => {
      showBoardProject(projectId, tab);
    },
    [showBoardProject],
  );

  const setProjectTab = useCallback((tab: ProjectTab) => {
    setBoard((b) => ({ ...b, tab }));
    if (tab === 'emi') setEmiTouched(true);
  }, []);

  const setProjectFloor = useCallback((floor: number) => {
    setBoard((b) => ({ ...b, floor, unitNo: null }));
  }, []);

  const pickUnit = useCallback((unitNo: string | null) => {
    setBoard((b) => ({ ...b, unitNo }));
  }, []);

  const setVisitDate = useCallback((id: string | null) => {
    setVisitDateState(id);
  }, []);

  const toggleVisitStop = useCallback((projectId: string) => {
    setVisitSkipped((d) => {
      const next = { ...d };
      if (next[projectId]) delete next[projectId];
      else next[projectId] = true;
      return next;
    });
  }, []);

  const planVisitDay = useCallback((actionId?: string) => {
    const selected = engagedProjects;
    if (selected.length === 0) {
      appendBotMessages([
        {
          id: nextId(),
          role: 'bot',
          text: 'Explore at least one project first — tap a match or ask me about it, then plan your visit day.',
        },
      ]);
      return;
    }

    setVisitSkipped((prev) => {
      const next = { ...prev };
      for (const p of selected) delete next[p.id];
      return next;
    });
    setBoardView('visit');

    const names = selected.map((p) => p.name);
    const userLine =
      names.length === 1 ? `Plan a visit to ${names[0]}` : `Plan visits to ${names.join(', ')}`;

    setMessages((m) => [...m, { id: nextId(), role: 'user', text: userLine }]);

    if (mode === 'live') {
      void dispatchLiveTurn(userLine, undefined, undefined, { boardVisitMode: true });
    } else {
      appendBotMessages([
        { id: nextId(), role: 'bot', text: visitBoardPlanAck(names) },
      ]);
    }
  }, [engagedProjects, setBoardView, mode, dispatchLiveTurn, appendBotMessages]);

  const setEmiField = useCallback((field: 'down' | 'rate' | 'tenure', value: number) => {
    setEmi((e) => ({ ...e, [field]: value }));
    setEmiTouched(true);
  }, []);

  const sendText = useCallback(
    (text: string, actionId?: string) => {
      const locations = briefOptions.location ?? [];

      if (mode === 'live' && preferenceEditingActive) {
        setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
        void dispatchLiveTurn(text, undefined, prefs, { matchesHub: true });
        return;
      }

      if (mode === 'live' && uiMode === 'brief_collect' && briefPhase === 'collecting') {
        // Chip-only brief: free text used to hit dispatchBriefExtract and ack
        // prefs without a shortlist (Cowork mix of brief + chat). Composer is
        // locked in the UI; ignore any stray send during collecting.
        return;
      }

      if (/\b(?:plan (?:a )?visit|visit day|site visit|plan visits)\b/i.test(text.trim())) {
        planVisitDay();
        return;
      }

      const catalogRefs = projectRefsFromCatalog(
        catalogPoolRef.current.length > 0 ? catalogPoolRef.current : matchProjects,
      );
      const multiVisitStops = resolveMultiProjectMentions(text, catalogRefs);
      if (isMultiVisitRequestTurn(text, catalogRefs) && multiVisitStops.length >= 1) {
        setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
        setVisitSkipped((prev) => {
          const next = { ...prev };
          for (const stop of multiVisitStops) delete next[stop.id];
          return next;
        });
        setBoardView('visit');
        if (mode === 'live') {
          void dispatchLiveTurn(text, undefined, undefined, { boardVisitMode: true });
          return;
        }
        for (const stop of multiVisitStops) markEngaged(stop.id);
        appendBotMessages([
          {
            id: nextId(),
            role: 'bot',
            text: visitBoardPlanAck(multiVisitStops.map((s) => s.name)),
          },
        ]);
        return;
      }

      if (board.view === 'visit' && isVisitRouteExpandTurn(text)) {
        const added = resolveProjectMention(text, catalogRefs);
        if (added) {
          setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
          markEngaged(added.id);
          setVisitSkipped((prev) => {
            const next = { ...prev };
            delete next[added.id];
            return next;
          });
          if (mode === 'live') {
            void dispatchLiveTurn(text, undefined, undefined, { boardVisitMode: true });
          } else {
            appendBotMessages([
              {
                id: nextId(),
                role: 'bot',
                text: `Added ${added.name} to your visit route — check the board and pick a Saturday when ready.`,
              },
            ]);
          }
          return;
        }
      }

      if (board.view === 'visit' && isVisitBoardSchedulingTurn(text) && mode !== 'live') {
        setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
        const dateId =
          resolveVisitDateFromText(text, VISIT_DATES) ?? visitDate ?? VISIT_DATES[0]?.id ?? null;
        if (dateId) setVisitDateState(dateId);
        const dateLabel = VISIT_DATES.find((d) => d.id === dateId)?.label;
        appendBotMessages([
          {
            id: nextId(),
            role: 'bot',
            text: visitBoardSchedulingReply(text, dateLabel),
          },
        ]);
        return;
      }

      if (mode !== 'live' && (briefPhase !== 'collecting' || board.view === 'project')) {
        const locs = briefOptions.location ?? [];
        if (isLocationExpandTurn(text, locs) || isSearchRefinementTurn(text, locs)) {
          const loc = extractLocationRefinement(text, locs);
          if (loc) {
            refineSearch(`Show me projects in ${loc}`, (p) =>
              applyRefinePatch(p, { kind: 'location', value: loc, userLine: '' }),
            );
            return;
          }
          if (/\b(?:widen|increase|raise|bump|expand)\s+(?:my\s+)?budget\b/i.test(text)) {
            const next = nextBudgetTier(prefs.budget, briefOptions.budget ?? []);
            if (next) {
              refineSearch(`Show me projects with budget up to ${next}`, (p) => ({ ...p, budget: next }));
              return;
            }
          }
          const bhkMatch = /\b(?:change|switch)\s+(?:to\s+)?(\d\s*bhk)\b/i.exec(text);
          const bhkRaw = bhkMatch?.[1]?.replace(/\s+/g, ' ');
          const bhkCanon = briefOptions.bhk?.find(
            (b) => bhkRaw && b.toLowerCase() === bhkRaw.toLowerCase(),
          );
          if (bhkCanon) {
            refineSearch(`Show me ${bhkCanon} projects that fit my brief`, (p) =>
              applyRefinePatch(p, { kind: 'bhk', value: bhkCanon, userLine: '' }),
            );
            return;
          }
        }
      }

      if (shouldClearMatchFocus(text, briefOptions.location ?? [])) {
        showAllMatches({ clearFocus: true });
      }

      setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);

      if (mode === 'live') {
        const locations = briefOptions.location ?? [];
        const matchesHub = shouldClearMatchFocus(text, locations);
        const onVisitBoard = board.view === 'visit';
        const refs = projectRefsFromCatalog(matchProjects);
        const stickyFocus =
          onVisitBoard && (isVisitBoardSchedulingTurn(text) || isVisitRouteExpandTurn(text))
            ? null
            : matchesHub || serverPhaseRef.current === 'visit' || isGlobalAdvisorTurn(text)
              ? null
              : board.projectId || null;
        const turnFocus = resolveTurnFocus(text, refs, stickyFocus);
        const prefsForTurn = isBriefReady(prefs, briefOptions) ? prefs : undefined;
        void dispatchLiveTurn(text, turnFocus, prefsForTurn, {
          boardVisitMode: !matchesHub && onVisitBoard,
          matchesHub,
          // ADR-006: deterministic chip — the door skips the LLM extract.
          ...(actionId ? { actionId } : {}),
        });
        return;
      }

      if (/\bcompare\b/i.test(text) && matchProjects.length >= 2) {
        openCompareBoard();
        appendBotMessages([
          {
            id: nextId(),
            role: 'bot',
            text: 'Side-by-side comparison is on your board →',
          },
        ]);
        return;
      }

      appendBotMessages(freeformReply(text));
    },
    [
      mode,
      uiMode,
      briefPhase,
      preferenceEditingActive,
      prefs,
      briefOptions,
      toggleMultiPref,
      setPref,
      dispatchLiveTurn,
      dispatchBriefExtract,
      scheduleBriefDispatch,
      appendBotMessages,
      refineSearch,
      board.view,
      board.projectId,
      visitDate,
      matchProjects,
      openCompareBoard,
      planVisitDay,
      showAllMatches,
      refineSearch,
      markEngaged,
      setBoardView,
    ],
  );

  const beginWithAsk = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      begin();
      if (trimmed) setPendingAsk(trimmed);
    },
    [begin],
  );

  // Drain the landing ask once the app view has mounted, so sendText sees settled
  // brief/board state and the message follows the exact same path as the composer.
  useEffect(() => {
    if (appView !== 'app' || pendingAsk === null) return;
    const text = pendingAsk;
    setPendingAsk(null);
    sendText(text);
  }, [appView, pendingAsk, sendText]);

  const openPlansDrawer = useCallback((projectId: string, unitId?: string | null) => {
    setDrawer({ kind: 'plans', projectId, unitId: unitId ?? null });
  }, []);

  const openShortlistDrawer = useCallback(() => {
    setDrawer({ kind: 'shortlist' });
  }, []);

  const openCompareDrawer = useCallback(() => {
    setDrawer({ kind: 'compare' });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer(EMPTY_DRAWER);
    const url = new URL(window.location.href);
    url.searchParams.delete('x');
    url.searchParams.delete('p');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  const runTool = useCallback(
    (projectId: string | undefined, kind: ToolKind) => {
      if (kind === 'floor_plan' && projectId) {
        if (mode === 'live') {
          void dispatchLiveTurn(`Show floor plan for ${projectId}`);
        } else {
          appendBotMessages(messagesForTool(projectId, kind));
        }
        openPlansDrawer(projectId);
        return;
      }
      if (mode === 'live') {
        const label = kind.replace('_', ' ');
        void dispatchLiveTurn(`${label}${projectId ? ` for ${projectId}` : ''}`);
      } else {
        appendBotMessages(messagesForTool(projectId, kind));
      }
      if (kind === 'compare_trends') {
        openCompareBoard();
      }
    },
    [mode, dispatchLiveTurn, appendBotMessages, openPlansDrawer, openCompareBoard],
  );

  const addToShortlist = useCallback(
    (project: Project, opts?: { silent?: boolean }) => {
      markEngaged(project.id);
      setShortlist((list) => {
        if (list.some((p) => p.id === project.id)) return list;
        const next = [...list, project];
        if (!opts?.silent) {
          appendBotMessages([shortlistAddedMessage(project.name, next.length)]);
        }
        return next;
      });
    },
    [appendBotMessages, markEngaged],
  );

  const removeFromShortlist = useCallback((projectId: string) => {
    setEngagedProjectIds((ids) => ids.filter((id) => id !== projectId));
    setShortlist((list) => list.filter((p) => p.id !== projectId));
    setUnitShortlist((list) => list.filter((u) => u.projectId !== projectId));
  }, []);

  const addUnitToShortlist = useCallback((entry: ShortlistedUnit) => {
    setUnitShortlist((list) => {
      if (list.some((u) => u.id === entry.id)) return list;
      const next = [...list, entry];
      appendBotMessages([unitShortlistAddedMessage(entry.projectName, entry.unitNo, next.length)]);
      return next;
    });
  }, [appendBotMessages]);

  const removeUnitFromShortlist = useCallback((unitId: string) => {
    setUnitShortlist((list) => list.filter((u) => u.id !== unitId));
  }, []);

  const isUnitShortlisted = useCallback(
    (unitId: string) => unitShortlist.some((u) => u.id === unitId),
    [unitShortlist],
  );

  const askCompare = useCallback(() => {
    compareAllMatches();
  }, [compareAllMatches]);

  const confirmReveal = useCallback((_project?: Project) => {
    setRevealed(true);
    appendBotMessages([revealConfirmedMessage()]);
  }, [appendBotMessages]);

  const resetSession = useCallback(() => {
    const newId = startNewAdvisorSession();
    setSessionId(newId);
    setAppView('app');
    setBoard(DEFAULT_BOARD);
    setRevealed(false);
    setVisitDateState(null);
    setVisitSkipped({});
    setEngagedProjectIds([]);
    setEmi({ down: 20, rate: 8.5, tenure: 20 });
    setEmiTouched(false);
    setMessages(BEGIN_MESSAGES.map((m) => ({ ...m, id: nextId() })));
    setPrefs(emptyPreferences());
    catalogPoolRef.current = [];
    setCatalogProjects([]);
    setBriefOptions(buildBriefOptions());
    setProjectDetails({});
    setProjectDetailLoading(null);
    setCompareMatrix(null);
    setBookedVisits([]);
    setVisitQueue(null);
    setVisitItinerary(null);
    setShortlist([]);
    setUnitShortlist([]);
    setDrawer(EMPTY_DRAWER);
    setBoardSheetOpen(false);
    setIsLoading(false);
    setUiMode('brief_collect');
    setLastSearchRecovery(null);
    setLastNbaChips(null);
    setLastNbaChipActions(null);
    setSignalSnapshot(null);
  }, []);

  const buyerPhone = useMemo(() => sessionToBuyerPhone(sessionId), [sessionId]);

  const value = useMemo(
    () => ({
      sessionId,
      appView,
      board,
      revealed,
      visitDate,
      visitSkipped,
      engagedProjectIds,
      bookedVisits,
      visitQueue,
      visitItinerary,
      visitRouteStopIds,
      visitAwaitingConfirm,
      emi,
      emiTouched,
      matchProjects,
      messages,
      prefs,
      shortlist,
      unitShortlist,
      isLoading,
      shortlisting,
      catalogCount,
      mode,
      briefReady,
      briefPhase,
      uiMode,
      preferenceEditingActive,
      lastSearchRecovery,
      lastNbaChips,
      lastNbaChipActions,
      signalSnapshot,
      editBriefAnswer,
      activeMultiPref,
      briefOptions,
      projectDetails,
      projectDetailLoading,
      compareMatrix,
      focusedProject,
      engagedProjects,
      getBoardProject,
      drawer,
      boardSheetOpen,
      setBoardSheetOpen,
      addReceipt,
      begin,
      beginWithAsk,
      goHome,
      setBoardView,
      showAllMatches,
      returnToMatchesHub,
      exploreMoreProjects,
      compareAllMatches,
      openCompareBoard,
      switchBoardFocus,
      openBoardProject,
      setProjectTab,
      setProjectFloor,
      pickUnit,
      setVisitDate,
      toggleVisitStop,
      planVisitDay,
      planSelectedVisits: planVisitDay,
      refineFromChip,
      beginBriefRefine,
      setEmiField,
      setPref,
      toggleMultiPref,
      confirmMultiPrefStep,
      sendText,
      addToShortlist,
      removeFromShortlist,
      addUnitToShortlist,
      removeUnitFromShortlist,
      isUnitShortlisted,
      askCompare,
      showRecommendations,
      confirmReveal,
      runTool,
      openPlansDrawer,
      openShortlistDrawer,
      openCompareDrawer,
      closeDrawer,
      resetSession,
      buyerPhone,
    }),
    [
      sessionId,
      buyerPhone,
      appView,
      board,
      revealed,
      visitDate,
      visitSkipped,
      engagedProjectIds,
      bookedVisits,
      visitQueue,
      visitItinerary,
      visitRouteStopIds,
      visitAwaitingConfirm,
      emi,
      emiTouched,
      matchProjects,
      messages,
      prefs,
      shortlist,
      unitShortlist,
      isLoading,
      shortlisting,
      catalogCount,
      mode,
      briefReady,
      briefPhase,
      uiMode,
      preferenceEditingActive,
      lastSearchRecovery,
      lastNbaChips,
      lastNbaChipActions,
      signalSnapshot,
      editBriefAnswer,
      activeMultiPref,
      briefOptions,
      projectDetails,
      projectDetailLoading,
      compareMatrix,
      focusedProject,
      engagedProjects,
      getBoardProject,
      drawer,
      boardSheetOpen,
      addReceipt,
      begin,
      beginWithAsk,
      goHome,
      setBoardView,
      showAllMatches,
      returnToMatchesHub,
      exploreMoreProjects,
      compareAllMatches,
      openCompareBoard,
      switchBoardFocus,
      openBoardProject,
      setProjectTab,
      setProjectFloor,
      pickUnit,
      setVisitDate,
      toggleVisitStop,
      planVisitDay,
      refineFromChip,
      beginBriefRefine,
      setEmiField,
      setPref,
      toggleMultiPref,
      confirmMultiPrefStep,
      sendText,
      addToShortlist,
      removeFromShortlist,
      addUnitToShortlist,
      removeUnitFromShortlist,
      isUnitShortlisted,
      askCompare,
      showRecommendations,
      confirmReveal,
      runTool,
      openPlansDrawer,
      openShortlistDrawer,
      openCompareDrawer,
      closeDrawer,
      resetSession,
    ],
  );

  return <AdvisorContext.Provider value={value}>{children}</AdvisorContext.Provider>;
}

export function useAdvisor() {
  const ctx = useContext(AdvisorContext);
  if (!ctx) throw new Error('useAdvisor must be used within AdvisorProvider');
  return ctx;
}

export function formatPrefsSummary(prefs: Preferences): string {
  const configParts = [...prefs.bhkSelections, ...prefs.plotSizeSelections];
  const parts = [
    prefs.propertyTypes.length ? prefs.propertyTypes.join(' · ') : null,
    configParts.length ? configParts.join(' · ') : prefs.bhk,
    prefs.location,
    prefs.budget,
  ].filter(Boolean);
  return parts.join(' · ') || 'Anonymous session';
}
